import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import {
  ANIMATION_FINISHED,
  ANIMATION_STOPPED,
  INITIALIZED,
  UPDATE_ANIMATION_STATE,
} from '../TextParticles.types';
import type {
  WorkerMessage,
  Message,
  PLAY,
  EffectConfiguration,
  AnimationState,
} from '../TextParticles.types';
import type { getMainThreadMessages } from '../worker/messages';
import { isImageTransferred } from '../utils/image-utils';
import { TARGET_TOTAL_PARTICLE_COUNT } from '../../constants';
import { getDevicePixelRatio } from '../utils/canvas-utils';

export type UseParticleRendererProps = {
  images: { imageBitmap: ImageBitmap; highResImageBitmap: ImageBitmap } | null;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  onAnimationEnd: () => void;
  onAnimationStateUpdated: (animationState: AnimationState) => void;
  componentId: string;
  worker: Worker | null;
  messages: ReturnType<typeof getMainThreadMessages>;
  onInitialized: () => void;
  onAnimationStopped: () => void;
  effectConfiguration: EffectConfiguration | null;
};

export const useParticleRenderer = ({
  effectConfiguration,
  canvasRef,
  images,
  onAnimationEnd,
  componentId,
  worker,
  messages,
  onInitialized,
  onAnimationStopped,
  onAnimationStateUpdated,
}: UseParticleRendererProps) => {
  const devicePixelRatio = useMemo(() => getDevicePixelRatio(), []);
  const [workerState, setWorkerState] = useState<'idle' | 'pending' | 'ready'>(
    'idle',
  );
  const currentAnimationPromiseRef = useRef<Promise<void> | null>(null);
  const currentAnimationResolverRef = useRef<(() => void) | null>(null);
  const pendingPlayMessageRef = useRef<Message<typeof PLAY> | null>(null);

  const playRef = useRef<() => Promise<void>>(async () => {});
  const stopRef = useRef<() => void>(() => {});
  const pauseRef = useRef<() => void>(() => {});

  const pendingImageRef = useRef<{
    imageBitmap: ImageBitmap;
    highResImageBitmap: ImageBitmap;
  } | null>(null);
  const pendingEffectConfigRef = useRef<EffectConfiguration | null>(null);
  const updateTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleAnimationStopped = useCallback(() => {
    if (currentAnimationResolverRef.current) {
      currentAnimationResolverRef.current();
      currentAnimationResolverRef.current = null;
    }
    currentAnimationPromiseRef.current = null;
    pendingPlayMessageRef.current = null;
    onAnimationStopped();
  }, [onAnimationStopped]);

  const messageHandler = useCallback(
    (message: MessageEvent<WorkerMessage>) => {
      const { data } = message;
      if (componentId === data.componentId) {
        switch (data.type) {
          case INITIALIZED:
            onInitialized();
            setWorkerState('ready');
            break;
          case ANIMATION_STOPPED:
            handleAnimationStopped();
            break;
          case ANIMATION_FINISHED:
            onAnimationEnd();
            if (currentAnimationResolverRef.current) {
              currentAnimationResolverRef.current();
              currentAnimationResolverRef.current = null;
            }
            currentAnimationPromiseRef.current = null;
            break;
          case UPDATE_ANIMATION_STATE:
            onAnimationStateUpdated(data.payload);
            break;
        }
      }
    },
    [
      componentId,
      onAnimationEnd,
      onInitialized,
      handleAnimationStopped,
      onAnimationStateUpdated,
    ],
  );

  useEffect(() => {
    if (!worker) {
      return;
    }

    worker.addEventListener('message', messageHandler);

    return () => {
      worker.removeEventListener('message', messageHandler);
    };
  }, [messageHandler, worker]);

  const scheduleCoordinatedUpdate = useCallback(() => {
    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
    }

    // Some prop changes (for example color) cause both EffectConfiguration update and image update.
    // These happen at the same time, but we need the image to be updated first.
    // We batch updates with a microtask to allow both image and config to be set in the appropriate order.
    updateTimerRef.current = setTimeout(() => {
      if (!worker || workerState !== 'ready') {
        return;
      }

      if (
        pendingImageRef.current !== null &&
        !isImageTransferred(pendingImageRef.current.imageBitmap)
      ) {
        worker.postMessage(
          messages.getUpdateImageMessage({
            imageBitmap: pendingImageRef.current.imageBitmap,
            highResImageBitmap: pendingImageRef.current.highResImageBitmap,
          }),
          [
            pendingImageRef.current.imageBitmap,
            pendingImageRef.current.highResImageBitmap,
          ],
        );
        pendingImageRef.current = null;
      }

      if (pendingEffectConfigRef.current !== null) {
        worker.postMessage(
          messages.getUpdateRendererPropsMessage(
            pendingEffectConfigRef.current,
          ),
        );
        pendingEffectConfigRef.current = null;
      }

      updateTimerRef.current = null;
    }, 0);
  }, [worker, messages, workerState]);

  useEffect(() => {
    if (!worker || workerState !== 'ready') {
      return;
    }

    if (pendingPlayMessageRef.current) {
      worker.postMessage(pendingPlayMessageRef.current);
      pendingPlayMessageRef.current = null;
    }
  }, [worker, workerState]);

  useEffect(() => {
    if (!worker || workerState !== 'ready' || !effectConfiguration) {
      return;
    }

    // Store pending config update
    pendingEffectConfigRef.current = effectConfiguration;
    scheduleCoordinatedUpdate();
  }, [worker, workerState, effectConfiguration, scheduleCoordinatedUpdate]);
  useEffect(() => {
    if (
      !canvasRef.current ||
      !worker ||
      workerState === 'ready' ||
      workerState === 'pending' ||
      !images ||
      isImageTransferred(images.imageBitmap) ||
      !effectConfiguration
    ) {
      return;
    }

    setWorkerState('pending');
    const transferredCanvas = canvasRef.current.transferControlToOffscreen();

    worker.postMessage(
      messages.getInitializeMessage({
        imageBitmap: images.imageBitmap,
        highResImageBitmap: images.highResImageBitmap,
        canvas: transferredCanvas,
        effectConfiguration,
        devicePixelRatio,
        targetParticleCount: TARGET_TOTAL_PARTICLE_COUNT,
      }),
      [images.imageBitmap, images.highResImageBitmap, transferredCanvas],
    );
  }, [
    canvasRef,
    workerState,
    images,
    worker,
    componentId,
    messages,
    effectConfiguration,
    devicePixelRatio,
  ]);

  useEffect(() => {
    if (
      !worker ||
      workerState !== 'ready' ||
      !images ||
      isImageTransferred(images.imageBitmap)
    ) {
      return;
    }

    if (pendingImageRef.current && pendingImageRef.current !== images) {
      pendingImageRef.current.imageBitmap.close();
      pendingImageRef.current.highResImageBitmap.close();
    }

    // Store pending image update
    pendingImageRef.current = images;
    scheduleCoordinatedUpdate();
  }, [componentId, images, workerState, worker, scheduleCoordinatedUpdate]);

  useEffect(() => {
    playRef.current = (): Promise<void> => {
      if (currentAnimationPromiseRef.current) {
        return currentAnimationPromiseRef.current;
      }

      const animationPromise = new Promise<void>(resolve => {
        currentAnimationResolverRef.current = resolve;

        const playMessage = messages.getPlayMessage();

        if (worker && workerState === 'ready') {
          worker.postMessage(playMessage);
        } else {
          pendingPlayMessageRef.current = playMessage;
        }
      });

      currentAnimationPromiseRef.current = animationPromise;

      animationPromise.finally(() => {
        if (currentAnimationPromiseRef.current === animationPromise) {
          currentAnimationPromiseRef.current = null;
        }
      });

      return animationPromise;
    };
  }, [worker, messages, workerState]);

  useEffect(() => {
    stopRef.current = () => {
      pendingPlayMessageRef.current = null;

      if (worker && workerState === 'ready') {
        worker.postMessage(messages.getStopMessage());
      }

      if (currentAnimationResolverRef.current) {
        currentAnimationResolverRef.current();
        currentAnimationResolverRef.current = null;
      }
      currentAnimationPromiseRef.current = null;
    };

    pauseRef.current = () => {
      pendingPlayMessageRef.current = null;

      if (worker && workerState === 'ready') {
        worker.postMessage(messages.getPauseMessage());
      }

      if (currentAnimationResolverRef.current) {
        currentAnimationResolverRef.current();
        currentAnimationResolverRef.current = null;
      }
      currentAnimationPromiseRef.current = null;
    };
  }, [worker, workerState, messages]);

  const play = useCallback(async () => playRef.current(), []);

  const stop = useCallback(() => stopRef.current(), []);

  const pause = useCallback(() => pauseRef.current(), []);

  useEffect(() => {
    return () => {
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
        updateTimerRef.current = null;
      }

      if (pendingImageRef.current) {
        pendingImageRef.current.imageBitmap.close();
        pendingImageRef.current.highResImageBitmap.close();
        pendingImageRef.current = null;
      }
    };
  }, []);

  return {
    play,
    stop,
    pause,
  };
};
